/**
 * PersonPicker — choose someone from the organization by name or email
 * (#2095).
 *
 * Replaces the raw-user-id text boxes on the two forms that add people
 * to things: granting workspace access, and adding a team member. An
 * admin should never have to obtain a 24-character database id from
 * outside the product to add a teammate.
 *
 * Where the list comes from. The organization's existing member
 * endpoint already returns members with their user info attached, and
 * it is readable by any org member — a strictly broader audience than
 * either action this picker feeds. So there is no new endpoint and no
 * new permission here, and matching happens over an already-loaded
 * list rather than a per-keystroke request. Authorization stays where
 * it already lives: on the grant itself, which keeps its own gate.
 *
 * The scale assumption is deliberate and worth knowing: the whole
 * member list is fetched once and filtered in the browser. That suits
 * organizations of realistic size. Past roughly a few hundred members
 * this should become a server-side search with paging.
 *
 * Rows people already hold are shown rather than hidden. Someone
 * searching for a colleague who is already a member needs an answer,
 * and an empty result is not one — so the row appears, marked and
 * unpickable, via `SearchableSelect`'s disabled-option support.
 */
import { useEffect, useMemo, useState } from 'react'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui'
import type { MemberWithUser, UserDisplayMap } from '@/lib/api'
import { orgsAPI } from '@/lib/api'
import { userDisplayName } from '@/lib/userDisplay'

export interface PersonPickerProps {
  /** Organization whose members are offered. */
  orgId: string
  /** Selected userId, or `''` when nothing is chosen yet. */
  value: string
  onChange: (userId: string) => void
  /** People who already hold whatever is being granted. They render,
   * marked with `alreadyHasLabel`, but cannot be picked. */
  alreadyHasIds?: readonly string[]
  /** What to call that state, in the caller's own terms — "Already has
   * access" on a workspace, "Already in this team" on a team. */
  alreadyHasLabel?: string
  /** Text shown before anyone is chosen. */
  placeholder?: string
  id?: string
  'aria-label'?: string
  disabled?: boolean
}

export function PersonPicker({
  orgId,
  value,
  onChange,
  alreadyHasIds,
  alreadyHasLabel = 'Already added',
  placeholder = 'Search by name or email',
  id,
  'aria-label': ariaLabel,
  disabled,
}: PersonPickerProps) {
  const [members, setMembers] = useState<MemberWithUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    // Clear the previous organization's people before fetching the new
    // ones (PR #2098 Copilot review). Without this the picker keeps
    // offering the old org's members until the new request resolves —
    // briefly showing people from an organization the admin is no
    // longer working in, and skipping the loading state that disables
    // the control. Already `null` on first mount, so this only bites
    // when `orgId` changes under a live component.
    setMembers(null)
    void (async () => {
      try {
        const { members: list } = await orgsAPI.listMembers(orgId)
        if (!cancelled) setMembers(list)
      } catch (err) {
        if (!cancelled) {
          setMembers([])
          // The underlying message goes to the console for debugging;
          // what the admin reads stays plain (see the alert below).
          console.error('[PersonPicker] failed to load org members', err)
          setError(err instanceof Error ? err.message : String(err))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId])

  const taken = useMemo(() => new Set(alreadyHasIds ?? []), [alreadyHasIds])

  const options = useMemo<SearchableSelectOption[]>(() => {
    if (!members) return []
    return members
      .map((m) => {
        // Route the label through the shared fallback so a member with
        // no name reads the same here as everywhere else (#2094).
        const asMap: UserDisplayMap = {
          [m.userId]: { name: m.user?.name, email: m.user?.email },
        }
        const name = userDisplayName(asMap, m.userId)
        const email = m.user?.email
        const isTaken = taken.has(m.userId)

        // Second line. The email goes here — suppressed when it is
        // already serving as the label, so it can't appear twice on one
        // row — and an unavailable row appends why.
        const emailLine = email && email !== name ? email : null
        const secondLine = isTaken
          ? [emailLine, alreadyHasLabel].filter(Boolean).join(' · ')
          : emailLine

        return {
          value: m.userId,
          label: name,
          trailing: secondLine ?? undefined,
          // Searchable even though it may not be rendered: an admin may
          // know the address and not the name.
          searchText: email,
          disabled: isTaken,
        } satisfies SearchableSelectOption
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [members, taken, alreadyHasLabel])

  return (
    <div>
      <SearchableSelect
        id={id}
        aria-label={ariaLabel}
        value={value}
        onChange={onChange}
        options={options}
        // Names are not machine output — the model-picker default
        // would render them in monospace.
        optionClassName="font-sans"
        placeholder={members === null ? 'Loading people…' : placeholder}
        searchPlaceholder="Type a name or email"
        disabled={disabled || members === null}
      />
      {error && (
        // Deliberately does NOT include the thrown message. That text is
        // network and stack wording written for a developer, and this
        // line is read by someone trying to add a teammate. The detail
        // is logged to the console instead.
        <p className="mt-1 text-xs text-destructive" role="alert">
          We couldn't load the people in this organization. Check your connection and try again.
        </p>
      )}
      {!error && members !== null && members.length === 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          There is nobody else in this organization yet. Invite them first.
        </p>
      )}
    </div>
  )
}
